/**
 * SOFT_DELETE_STANDARD (F4-A) — restaurar desde la papelera.
 *
 * Pasa por el write-path normal de PowerSync (la rama del server valida el
 * acceso y registra `restored` en activity). La papelera de Space es
 * RESTORE-ONLY a propósito (variante field-check de la guía): el write-path
 * convierte el DELETE del cliente en soft-delete server-side, y el borrado
 * físico lo hace SOLO el job de purga (`TRASH_PURGE_DAYS`, default 30 días)
 * dejando `purged` con snapshot en el log.
 */
import {
  assignment,
  course_module,
  eq,
  lesson,
  type PowerSyncDrizzleDB,
  resource,
} from "@space/powersync";
import { nowISO } from "../utils/date-helpers";
import { TRASH_TABLES, type TrashKind } from "./queries";

/** Restaura una fila de la papelera (deleted_at → NULL). */
export async function restoreTrashedMutation(
  db: PowerSyncDrizzleDB,
  kind: TrashKind,
  id: string,
): Promise<void> {
  const table = TRASH_TABLES[kind];
  await db.update(table).set({ deleted_at: null, updated_at: nowISO() }).where(eq(table.id, id));
}

/** Re-envía una fila viva a la papelera (inversa del restore, para undo L1). */
export async function trashAgainMutation(
  db: PowerSyncDrizzleDB,
  kind: TrashKind,
  id: string,
): Promise<void> {
  const table = TRASH_TABLES[kind];
  await db
    .update(table)
    .set({ deleted_at: nowISO(), updated_at: nowISO() })
    .where(eq(table.id, id));
}

/** Ref soft-deletable de un módulo de curso (borrado/restauración en pareja). */
function moduleRefTable(
  kind: string | null,
): typeof lesson | typeof resource | typeof assignment | null {
  switch (kind) {
    case "lesson": {
      return lesson;
    }
    case "resource": {
      return resource;
    }
    case "assignment": {
      return assignment;
    }
    default: {
      return null;
    }
  }
}

/**
 * Restaura un `course_module` JUNTO a su fila referenciada (lesson/resource/
 * assignment): el flujo de borrado del authoring soft-borra ambas — restaurar
 * solo el módulo dejaría el contenido huérfano-oculto.
 */
export async function restoreCourseModuleCascadeMutation(
  db: PowerSyncDrizzleDB,
  moduleId: string,
): Promise<void> {
  const rows = await db.select().from(course_module).where(eq(course_module.id, moduleId)).limit(1);
  await db
    .update(course_module)
    .set({ deleted_at: null, updated_at: nowISO() })
    .where(eq(course_module.id, moduleId));
  const mod = rows[0];
  const ref = moduleRefTable(mod?.kind ?? null);
  if (mod?.ref_id && ref) {
    await db
      .update(ref)
      .set({ deleted_at: null, updated_at: nowISO() })
      .where(eq(ref.id, mod.ref_id));
  }
}

/** Inversa del restore en cascada (undo L1): re-borra módulo + ref. */
export async function trashCourseModuleCascadeMutation(
  db: PowerSyncDrizzleDB,
  moduleId: string,
): Promise<void> {
  const rows = await db.select().from(course_module).where(eq(course_module.id, moduleId)).limit(1);
  const now = nowISO();
  await db
    .update(course_module)
    .set({ deleted_at: now, updated_at: now })
    .where(eq(course_module.id, moduleId));
  const mod = rows[0];
  const ref = moduleRefTable(mod?.kind ?? null);
  if (mod?.ref_id && ref) {
    await db.update(ref).set({ deleted_at: now, updated_at: now }).where(eq(ref.id, mod.ref_id));
  }
}
